export const TAB_NAV_ENABLED =
  process.env.REACT_APP_TAB_NAVIGATION !== "false";

export const TAB_BAR_STICKY_HEIGHT = 22;

export const HOME_TAB_PATH = "/home";

export const AUTH_ONLY_PATHS = ["/login", "/forgetPassword", "/health"];

const STATIC_ASSET_PREFIXES = ["/download/", "/upload/", "/media/"];

export function isAuthOnlyPath(pathname) {
  const path = normalizePathname(pathname);
  return AUTH_ONLY_PATHS.includes(path);
}

export function isStaticAssetPath(pathname) {
  const path = normalizePathname(pathname);
  return STATIC_ASSET_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function normalizePathname(pathname) {
  if (!pathname) return HOME_TAB_PATH;
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

export function normalizeSearch(search = "") {
  if (!search) return "";
  return search.startsWith("?") ? search : `?${search}`;
}

export function buildTabKey(pathname, search = "") {
  const path = normalizePathname(pathname);
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  const id = params.get("id");
  if (id) return `${path}?id=${id}`;
  if (params.get("mode") === "add") return `${path}?mode=add`;
  return path;
}

export function buildTabLocation(pathname, search = "") {
  return `${normalizePathname(pathname)}${normalizeSearch(search)}`;
}

export function findOpenDetailTab(tabs, pathname, id) {
  if (!tabs?.length || !pathname || !id) return null;
  const key = buildTabKey(pathname, `?id=${id}`);
  return tabs.find((tab) => tab.key === key) ?? null;
}

/** Remount an open detail tab so its form reloads from the server. */
export function refreshOpenDetailTab(tabActions, pathname, id) {
  if (!tabActions?.enabled || !pathname || !id) return false;
  const tab = findOpenDetailTab(tabActions.getTabs(), pathname, id);
  if (!tab) return false;
  tabActions.refreshTab(tab.id);
  return true;
}

function truncateLabel(text, maxLength) {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) : text;
}

export function getClientTabLabel(data, fallbackId) {
  if (!data) return fallbackId?.slice?.(-8) || "Detail";
  const code = data.code;
  const name = data.name || data.cname || data.aliasName;
  if (code && name) {
    return `${code} ${truncateLabel(name, 15)}`.trim();
  }
  return getEntityTabLabel(data, fallbackId);
}

export function getEntityTabLabel(data, fallbackId, entityType) {
  if (entityType === "client") {
    return getClientTabLabel(data, fallbackId);
  }
  if (!data) return fallbackId?.slice?.(-8) || "Detail";
  const candidates = [
    data.debitNoteNo,
    data.code,
    data.name,
    data.policyNumber,
    data.staffId,
    data.aliasName,
    data.id,
  ].filter(Boolean);
  if (data.cname && data.name) {
    candidates.unshift(`${data.name} ${data.cname}`.trim());
  }
  return candidates[0] || fallbackId?.slice?.(-8) || "Detail";
}

export function getDefaultLabelForPath(pathname) {
  const segments = normalizePathname(pathname).split("/").filter(Boolean);
  const last = segments[segments.length - 1] || "Home";
  return last
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

export function createTabId() {
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const LEGACY_TAB_STORAGE_KEY = "mbid-tab-state";
const PERSIST_DEBOUNCE_MS = 300;

let memoryTabStateByUser = {};
let persistTimer = null;
let pendingPersist = null;

export function getTabStorageKey(userId) {
  if (!userId) return null;
  return `mbid-tab-state-${userId}`;
}

function isValidPersistedTab(tab) {
  return (
    tab &&
    typeof tab.id === "string" &&
    typeof tab.key === "string" &&
    typeof tab.pathname === "string" &&
    typeof tab.label === "string"
  );
}

function sanitizePersistedTabs(tabs) {
  if (!Array.isArray(tabs)) return [];
  return tabs.filter(isValidPersistedTab);
}

function readStorageItem(storage, key) {
  if (!storage || !key) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageItem(storage, key, value) {
  if (!storage || !key) return;
  try {
    storage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
}

function removeStorageItem(storage, key) {
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore storage errors
  }
}

function migrateLegacySessionStorage(userId) {
  const storageKey = getTabStorageKey(userId);
  if (!storageKey) return null;

  const legacyRaw = readStorageItem(sessionStorage, LEGACY_TAB_STORAGE_KEY);
  if (!legacyRaw) return null;

  const existingRaw = readStorageItem(localStorage, storageKey);
  if (!existingRaw) {
    writeStorageItem(localStorage, storageKey, legacyRaw);
  }
  removeStorageItem(sessionStorage, LEGACY_TAB_STORAGE_KEY);
  return legacyRaw;
}

export function clearTabState(userId) {
  if (userId) {
    delete memoryTabStateByUser[userId];
  } else {
    memoryTabStateByUser = {};
  }

  if (pendingPersist?.userId === userId || !userId) {
    pendingPersist = null;
  }

  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }

  const storageKey = getTabStorageKey(userId);
  removeStorageItem(localStorage, storageKey);
  removeStorageItem(sessionStorage, LEGACY_TAB_STORAGE_KEY);
}

function normalizeHomeTab(tabs) {
  return tabs.map((tab) =>
    tab.id === "home-tab" || tab.pinned
      ? {
          ...createHomeTab(),
          ...tab,
          id: "home-tab",
          label: "Home",
          closable: false,
          pinned: true,
        }
      : tab
  );
}

function ensureHomeTab(tabs) {
  const normalized = normalizeHomeTab(tabs);
  const hasHome = normalized.some((tab) => tab.id === "home-tab" || tab.pinned);
  if (hasHome) return normalized;
  return [createHomeTab(), ...normalized];
}

function writePersistedState(userId, tabs, activeTabId) {
  if (!userId || !tabs?.length) return;
  const storageKey = getTabStorageKey(userId);
  if (!storageKey) return;

  const normalizedTabs = normalizeHomeTab(tabs);
  const state = { tabs: normalizedTabs, activeTabId };
  memoryTabStateByUser[userId] = state;
  writeStorageItem(localStorage, storageKey, JSON.stringify(state));
}

export function flushPersistTabState() {
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  if (!pendingPersist) return;
  const { userId, tabs, activeTabId } = pendingPersist;
  pendingPersist = null;
  writePersistedState(userId, tabs, activeTabId);
}

export function persistTabState(
  userId,
  tabs,
  activeTabId,
  { immediate = false } = {}
) {
  if (!userId || !tabs?.length) return;
  pendingPersist = { userId, tabs, activeTabId };
  memoryTabStateByUser[userId] = {
    tabs: normalizeHomeTab(tabs),
    activeTabId,
  };

  if (immediate) {
    flushPersistTabState();
    return;
  }

  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    flushPersistTabState();
  }, PERSIST_DEBOUNCE_MS);
}

export function loadPersistedTabState(userId) {
  if (!userId) return null;

  const cached = memoryTabStateByUser[userId];
  if (cached?.tabs?.length) {
    return cached;
  }

  try {
    const storageKey = getTabStorageKey(userId);
    let raw = readStorageItem(localStorage, storageKey);
    if (!raw) {
      raw = migrateLegacySessionStorage(userId);
    }
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const validTabs = sanitizePersistedTabs(parsed?.tabs);
    if (!validTabs.length) return null;

    const tabs = ensureHomeTab(validTabs);
    const activeTabId = tabs.some((tab) => tab.id === parsed.activeTabId)
      ? parsed.activeTabId
      : tabs[0].id;

    memoryTabStateByUser[userId] = { tabs, activeTabId };
    return memoryTabStateByUser[userId];
  } catch {
    return null;
  }
}

export function createHomeTab() {
  return {
    id: "home-tab",
    key: HOME_TAB_PATH,
    label: "Home",
    pathname: HOME_TAB_PATH,
    search: "",
    closable: false,
    pinned: true,
  };
}

export function buildInitialTabState(userId, location = window.location) {
  const { pathname, search } = location;
  const normalizedPath = normalizePathname(pathname);

  if (isStaticAssetPath(normalizedPath)) {
    const homeTab = createHomeTab();
    return { tabs: [homeTab], activeTabId: homeTab.id };
  }

  const persisted = loadPersistedTabState(userId);
  if (persisted) {
    return { ...persisted, restoredFromPersistence: true };
  }

  const homeTab = createHomeTab();

  if (
    (normalizedPath === "/" || normalizedPath === HOME_TAB_PATH) &&
    !search
  ) {
    return { tabs: [homeTab], activeTabId: homeTab.id };
  }

  const extraTab = {
    id: createTabId(),
    key: buildTabKey(normalizedPath, search),
    label: getDefaultLabelForPath(normalizedPath),
    pathname: normalizedPath,
    search: normalizeSearch(search),
    closable: true,
    pinned: false,
  };

  return {
    tabs: [homeTab, extraTab],
    activeTabId: extraTab.id,
  };
}
